import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { brandConfig } from '../../config/brands';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useGenerationAssetsSafe } from '../generation/state/assetsContext';
import { useGenerationJobsSafe } from '../generation/state/jobsContext';
import { NotificationContent } from '../notifications/NotificationContent';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { MenuContent } from './MenuContent';

import './MainNav.css';

const SEEN_STATE_KEY = 'notification_seen_state';

type ExpiringInfo = {
  amount: number;
  expires_at: string;
  days_until_expiry: number;
};

type MainNavProps = {
  /** 通知バッジを表示するか */
  hasNotification?: boolean;
  /** 失効予定のポイント情報 */
  expiringSoon?: ExpiringInfo | null;
  /** 管理者かどうか */
  isAdmin?: boolean;
  /** ログイン中かどうか */
  isLoggedIn?: boolean;
  /** ログアウト処理 */
  onLogout?: () => void;
  /** ポイント残高（モバイルメニュー表示用） */
  creditsBalance?: number | null;
};

export default function MainNav({
  hasNotification = false,
  expiringSoon,
  isAdmin = false,
  isLoggedIn = false,
  onLogout = () => {},
  creditsBalance,
}: MainNavProps) {
  const { close } = useMaterialsPanelStore();
  const isMobile = useIsMobile();
  // 選択素材数は Assets、ジョブ履歴の起動は Jobs だけを購読する
  // (設定・属性ツリーの更新ではナビ全体を再レンダーしない)。
  const assetsState = useGenerationAssetsSafe();
  const jobsState = useGenerationJobsSafe();
  const selectedAssetCount = assetsState?.selectedAssets?.length ?? 0;
  const { data: announcements } = useAnnouncements();
  const [isNotificationOpen, setNotificationOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 確認済みの状態スナップショット
  const [seenState, setSeenState] = useState<string | null>(() => {
    return localStorage.getItem(SEEN_STATE_KEY);
  });

  // 現在の通知状態のキーを生成
  const currentStateKey = useMemo(() => {
    return JSON.stringify({
      expiring: hasNotification ? expiringSoon?.amount ?? 0 : 0,
      latestAnnouncementId: announcements?.[0]?.id || null,
    });
  }, [hasNotification, expiringSoon?.amount, announcements]);

  // バッジ表示判定（確認済みと異なる場合のみ）
  const hasUnseenNotifications = useMemo(() => {
    const hasAny = hasNotification || (announcements && announcements.length > 0);
    if (!hasAny) return false;
    if (seenState === currentStateKey) return false;
    return true;
  }, [hasNotification, announcements, seenState, currentStateKey]);

  // 通知ポップアップを開いた時に状態を保存
  const markAsSeen = useCallback(() => {
    localStorage.setItem(SEEN_STATE_KEY, currentStateKey);
    setSeenState(currentStateKey);
  }, [currentStateKey]);

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (isNotificationOpen || isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen, isMenuOpen]);

  // ポップアップが開いている間は背景スクロール無効化＋コンテンツブラー
  useEffect(() => {
    if (isNotificationOpen || isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mobile-popup-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-popup-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-popup-open');
    };
  }, [isNotificationOpen, isMenuOpen]);

  // テンプレート特化ブランド (usodeki) はスタジオ / 商品データ / AI俳優 のタブを出さない。
  // ただし「素材」タブだけは materialsNavTab フラグで表示できる (hypeapp は従来どおり PC のみ表示)
  const showNavTabs = brandConfig.features.mainNavTabs;
  const showMaterialsTab = brandConfig.features.materialsNavTab ?? false;

  const navContent = isMobile ? (
    <nav className="main-nav main-nav--mobile">
      {showNavTabs && (
        <>
          <NavLink
            to="/studio"
            className={({ isActive }) => `mobile-tab ${isActive ? 'is-active' : ''}`}
          >
            <div className="mobile-tab-icon-wrapper">
              <img src="/composition_focus_square.svg" alt="" className="mobile-tab-icon" />
              {selectedAssetCount > 0 && (
                <span className="mobile-selection-badge">{selectedAssetCount}</span>
              )}
            </div>
            <span className="mobile-tab-label">{brandConfig.labels.studioShort}</span>
          </NavLink>
          <NavLink
            to="/products"
            className={({ isActive }) => `mobile-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => close()}
          >
            <img src="/database_2.svg" alt="" className="mobile-tab-icon" />
            <span className="mobile-tab-label">{brandConfig.labels.products}</span>
          </NavLink>
          <NavLink
            to="/exclusive-actors"
            className={({ isActive }) => `mobile-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => close()}
          >
            <img src="/composition_window_man.svg" alt="" className="mobile-tab-icon" />
            <span className="mobile-tab-label">{brandConfig.labels.aiActors}</span>
          </NavLink>
        </>
      )}
      {!showNavTabs && showMaterialsTab && (
        <NavLink
          to="/materials"
          className={({ isActive }) => `mobile-tab ${isActive ? 'is-active' : ''}`}
          onClick={() => close()}
        >
          <img src="/picture_stack_landscape.svg" alt="" className="mobile-tab-icon" />
          <span className="mobile-tab-label">{brandConfig.labels.materials}</span>
        </NavLink>
      )}

      {/* 通知 */}
      <div className="mobile-tab-container" ref={notificationRef}>
        <button
          type="button"
          className={`mobile-tab ${isNotificationOpen ? 'is-active' : ''}`}
          onClick={() => {
            const wasOpen = isNotificationOpen;
            setNotificationOpen((prev) => !prev);
            setMenuOpen(false);
            // 開く時に既読にする
            if (!wasOpen) {
              markAsSeen();
            }
          }}
        >
          <div className="mobile-tab-icon-wrapper">
            <img src="/alert_bell_notification_2.svg" alt="" className="mobile-tab-icon" />
            {hasUnseenNotifications && <span className="mobile-notification-badge" />}
          </div>
          <span className="mobile-tab-label">通知</span>
        </button>
        {isNotificationOpen && (
          <>
            {/* オーバーレイ：背景タップで閉じる */}
            <div
              className="mobile-popup-overlay"
              onClick={() => setNotificationOpen(false)}
              aria-hidden="true"
            />
            <div className="mobile-popup mobile-popup--notification">
              <NotificationContent
                expiringSoon={expiringSoon}
                announcements={announcements}
                onLinkClick={() => setNotificationOpen(false)}
                onMarkAllAsRead={markAsSeen}
                onOpenJobHistory={jobsState ? () => {
                  setNotificationOpen(false);
                  jobsState.setShowJobQueueModal(true);
                } : undefined}
              />
            </div>
          </>
        )}
      </div>

      {/* メニュー */}
      <div className="mobile-tab-container" ref={menuRef}>
        <button
          type="button"
          className={`mobile-tab ${isMenuOpen ? 'is-active' : ''}`}
          onClick={() => {
            setMenuOpen((prev) => !prev);
            setNotificationOpen(false);
          }}
        >
          <img src="/navigation_menu.svg" alt="" className="mobile-tab-icon" />
          <span className="mobile-tab-label">メニュー</span>
        </button>
        {isMenuOpen && (
          <>
          <div
            className="mobile-popup-overlay"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="mobile-popup mobile-popup--menu">
            <MenuContent
              isAdmin={isAdmin}
              isLoggedIn={isLoggedIn}
              onLogout={onLogout}
              creditsBalance={creditsBalance}
            />
          </div>
          </>
        )}
      </div>
    </nav>
  ) : (
    <nav className="main-nav">
      {showNavTabs && (
        <NavLink
          to="/studio"
          className={({ isActive }) => `nav-tab ${isActive ? 'is-active' : ''}`}
          data-guide-step="nav-studio"
        >
          {brandConfig.labels.studio}
          {selectedAssetCount > 0 && (
            <span className="nav-tab-badge">{selectedAssetCount}</span>
          )}
        </NavLink>
      )}
      {showMaterialsTab && (
        <NavLink
          to="/materials"
          className={({ isActive }) => `nav-tab ${isActive ? 'is-active' : ''}`}
          onClick={() => close()}
          data-guide-step="nav-materials"
        >
          {brandConfig.labels.materials}
        </NavLink>
      )}
      {showNavTabs && (
        <>
          <NavLink
            to="/products"
            className={({ isActive }) => `nav-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => close()}
            data-guide-step="nav-products"
          >
            {brandConfig.labels.products}
          </NavLink>
          <NavLink
            to="/exclusive-actors"
            className={({ isActive }) => `nav-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => close()}
            data-guide-step="nav-actors"
          >
            {brandConfig.labels.aiActors}
          </NavLink>
        </>
      )}
    </nav>
  );

  // スマホ時はポータルで専用コンテナにレンダリング
  if (isMobile) {
    const mobileNavRoot = document.getElementById('mobile-nav-root');
    if (mobileNavRoot) {
      return createPortal(navContent, mobileNavRoot);
    }
  }

  return navContent;
}
